import type { SupSingleVaultV3 } from "@/types";
import { useMemo } from "react";

import getUnixTime from "date-fns/getUnixTime";
import { utcDay } from "d3-time";

import dictionary from "@/../config/dictionary.config";
import useApi from "@/hooks/api/useApi";

export interface VaultHistoricalData {
  timestamp: number[];
  apy: (number | null)[];
  apy_base: (number | null)[];
  pps: (number | null)[];
  pps_usd: (number | null)[];
  tvl: (number | null)[];
}

export interface ResponseVaultHistoricalData {
  vaults: Record<string, VaultHistoricalData>;
}

export interface SupVaultHistoricalData {
  tvl: number | null;
  date: Date;
  apy: number | null;
  apy_base: number | null;
  pps: number | null;
  pps_usd: number | null;
  timestamp: number;
  name: string;
}

interface useVaultHistoricalDataProps {
  vault: SupSingleVaultV3;
  startDate?: Date;
  endDate?: Date;
}

/**
 * @package api:swr
 * @link https://superform-api-dev.herokuapp.com/docs#/4626%20Vault%20Data/get_historical_vault_data_vault_data_historical__vault_id__get
 *
 */
export function useVaultHistoricalData({ vault, startDate, endDate }: useVaultHistoricalDataProps) {
  const { params, path } = useMemo(() => {
    return {
      path: `/vault/data/historical/${vault.id}/`,
      params: {
        start_timestamp: endDate ? getUnixTime(endDate) : undefined,
        end_timestamp: startDate ? getUnixTime(startDate) : undefined,
        granularity: "day",
        ignore_nan: true,
      },
    };
  }, [vault, startDate, endDate]);

  const { data, isLoading, isError, mutate } = useApi<SupVaultHistoricalData[], ResponseVaultHistoricalData>(
    {
      method: "GET",
      path,
      params,
      api: "v2",
    },
    {
      strategy: "on-mount",
      select: (data: ResponseVaultHistoricalData): SupVaultHistoricalData[] => {
        const vaultData = data.vaults[vault.id];
        return vaultData.timestamp
          .map((timestamp, index: number) => {
            const ts = timestamp * 1000;
            const date = new Date(ts);
            const formattedDate = utcDay.floor(date);

            return {
              name: date.toLocaleDateString("en-US", { month: "numeric", day: "numeric" }),
              apy: vaultData.apy[index] != null && vaultData.apy[index] !== dictionary.statsDefaultValue ? vaultData.apy[index] : null,
              apy_base: vaultData.apy_base[index] != null && vaultData.apy_base[index] !== dictionary.statsDefaultValue ? vaultData.apy_base[index] : null,
              pps: vaultData.pps[index] != null && vaultData.pps[index] !== dictionary.statsDefaultValue ? vaultData.pps[index] : null,
              pps_usd: vaultData.pps_usd[index] != null && vaultData.pps_usd[index] !== dictionary.statsDefaultValue ? vaultData.pps_usd[index] : null,
              tvl: vaultData.tvl[index] != null && vaultData.tvl[index] !== dictionary.statsDefaultValue ? vaultData.tvl[index] : null,
              timestamp: formattedDate.getTime(),
              date: formattedDate,
            };
          })
          .filter(({ apy, tvl }) => apy != null || tvl != null);
      },
    }
  );

  return {
    data,
    isLoading,
    isError,
    mutate,
  };
}
